import type { ReportData } from '../../../hooks/useReports';

interface Props {
  data: ReportData;
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v: number) => `${v}%`;

export const ReportSummaryCards = ({ data, loading }: Props) => {
  const totalOrigin = data.distribConta + data.distribCartao;
  const pctConta   = totalOrigin > 0 ? Math.round((data.distribConta / totalOrigin) * 100) : 0;
  const pctCartao  = totalOrigin > 0 ? Math.round((data.distribCartao / totalOrigin) * 100) : 0;

  const cards = [
    {
      label: 'Total Receitas',
      value: fmt(data.totalReceitas),
      sub: `vs. ${fmt(data.prevPeriodReceitas)} anterior`,
      icon: 'ri-arrow-up-circle-line',
      bg: 'bg-emerald-50 border-emerald-200',
      text: 'text-emerald-600',
      ibg: 'bg-emerald-100',
      trend: data.prevPeriodReceitas > 0 ? data.totalReceitas - data.prevPeriodReceitas : null,
    },
    {
      label: 'Total Despesas',
      value: fmt(data.totalDespesas),
      sub: `vs. ${fmt(data.prevPeriodDespesas)} anterior`,
      icon: 'ri-arrow-down-circle-line',
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-500',
      ibg: 'bg-red-100',
      trend: data.prevPeriodDespesas > 0 ? data.totalDespesas - data.prevPeriodDespesas : null,
      trendInvert: true,
    },
    {
      label: 'Saldo do Período',
      value: fmt(data.saldo),
      sub: data.saldo >= 0 ? 'Positivo — bom controle' : 'Negativo — atenção',
      icon: data.saldo >= 0 ? 'ri-wallet-3-line' : 'ri-alarm-warning-line',
      bg: data.saldo >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200',
      text: data.saldo >= 0 ? 'text-emerald-600' : 'text-orange-500',
      ibg: data.saldo >= 0 ? 'bg-emerald-100' : 'bg-orange-100',
      trend: null,
    },
    {
      label: 'Lançamentos',
      value: String(data.transactionCount),
      sub: `Ticket médio: ${fmt(data.ticketMedio)}`,
      icon: 'ri-list-check-3',
      bg: 'bg-gray-50 border-gray-200',
      text: 'text-gray-700',
      ibg: 'bg-gray-100',
      trend: null,
      isCount: true,
    },
    {
      label: 'Total Pendente',
      value: fmt(data.totalPendente),
      sub: `Vencido: ${fmt(data.overdueTotal)}`,
      icon: 'ri-time-line',
      bg: data.totalPendente > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200',
      text: data.totalPendente > 0 ? 'text-amber-600' : 'text-gray-500',
      ibg: data.totalPendente > 0 ? 'bg-amber-100' : 'bg-gray-100',
      trend: null,
    },
    {
      label: 'Maior Categoria',
      value: data.topCategoria,
      sub: data.expensesByCategory[0] ? `${fmt(data.expensesByCategory[0].total)} · ${data.expensesByCategory[0].percent}%` : 'Sem despesas',
      icon: 'ri-pie-chart-line',
      bg: 'bg-violet-50 border-violet-200',
      text: 'text-violet-600',
      ibg: 'bg-violet-100',
      trend: null,
      isText: true,
    },
    {
      label: '% Recorrentes',
      value: fmtPct(data.pctRecorrentes),
      sub: `Parcelados: ${fmtPct(data.pctParcelados)} das despesas`,
      icon: 'ri-repeat-line',
      bg: data.pctRecorrentes > 40 ? 'bg-orange-50 border-orange-200' : 'bg-sky-50 border-sky-200',
      text: data.pctRecorrentes > 40 ? 'text-orange-500' : 'text-sky-600',
      ibg: data.pctRecorrentes > 40 ? 'bg-orange-100' : 'bg-sky-100',
      trend: null,
      isText: true,
    },
    {
      label: 'Conta vs Cartão',
      value: `${pctConta}% / ${pctCartao}%`,
      sub: `${fmt(data.distribConta)} conta · ${fmt(data.distribCartao)} cartão`,
      icon: 'ri-bank-card-line',
      bg: 'bg-indigo-50 border-indigo-200',
      text: 'text-indigo-600',
      ibg: 'bg-indigo-100',
      trend: null,
      isText: true,
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cards.map(card => {
        const trendPct = card.trend != null && (card.trendInvert ? card.prevPeriodDespesas : card.prevPeriodReceitas)
          ? Math.round((card.trend! / (card.trendInvert ? card.prevPeriodDespesas! : card.prevPeriodReceitas!) * 100)) : null;
        return (
          <div key={card.label} className={`rounded-2xl border p-4 ${card.bg}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-gray-500 font-500 leading-tight">{card.label}</span>
              <div className={`w-7 h-7 flex items-center justify-center rounded-xl ${card.ibg}`}>
                <i className={`${card.icon} text-[15px] ${card.text}`} />
              </div>
            </div>
            <p className={`font-700 leading-tight ${card.isText ? 'text-[14px]' : 'text-[18px]'} ${card.text}`}>
              {card.value}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5 truncate">{card.sub}</p>
          </div>
        );
      })}
    </div>
  );
};
